import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import messages from './messages.json';

export const LANGUAGE_STORAGE_KEY = 'lia:ui-language';
export const supportedLanguages = ['it', 'en'];
export const normalizeLanguage = value => /^en(?:-|$)/i.test(value || '') ? 'en' : 'it';
export const localeFor = language => normalizeLanguage(language) === 'en' ? 'en-GB' : 'it-IT';
const initialLanguage = () => {
  try { return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)); }
  catch { return 'it'; }
};
const resources = { it: {}, en: {} };
const bySource = new Map();
for (const message of messages) {
  const ns = message.namespace || 'common';
  resources.it[ns] ||= {}; resources.en[ns] ||= {};
  resources.it[ns][message.id] = message.source;
  resources.en[ns][message.id] = message.en;
  bySource.set(message.source, message);
}
export const i18n = i18next.createInstance();
i18n.use(initReactI18next).init({
  resources, lng: initialLanguage(), fallbackLng: 'it', supportedLngs: supportedLanguages,
  defaultNS: 'common', ns: Object.keys(resources.it), initAsync: false,
  interpolation: { escapeValue: false }, react: { useSuspense: false },
});
export const getLanguage = () => normalizeLanguage(i18n.resolvedLanguage || i18n.language);
export const getLocale = () => localeFor(getLanguage());
const syncDocument = language => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = localeFor(language);
  const description = document.querySelector('meta[name="description"]');
  if (description) description.content = normalizeLanguage(language) === 'en'
    ? 'Longevity Income Architect — Monte Carlo retirement income planner'
    : 'Longevity Income Architect — Simulatore Monte Carlo per strategie di reddito a lungo termine';
};
syncDocument(getLanguage());
i18n.on('languageChanged', language => {
  syncDocument(language);
  try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(language)); } catch { /* Session choice still works. */ }
});
export const changeLanguage = language => i18n.changeLanguage(normalizeLanguage(language));

// Original result objects, dataset records and saved descriptions remain immutable.
// This adapter also supports the exact descriptions of historical engine editions.
const normaliseText = text => text.replace(/\s+/g, ' ').trim();
const templates = messages.filter(m => /\{\{v\d+\}\}/.test(m.source)).map(message => {
  const keys = [...message.source.matchAll(/\{\{(v\d+)\}\}/g)].map(m => m[1]);
  const parts = message.source.split(/\{\{v\d+\}\}/g);
  return { message, keys, parts, prefix: parts[0], suffix: parts.at(-1), literalLength: parts.join('').length };
}).filter(t => t.literalLength >= 4 && t.parts.slice(1, -1).every(Boolean))
  .sort((a,b) => b.literalLength - a.literalLength);

// Linear matching: unbounded wildcard regexes can backtrack catastrophically
// on long archived descriptions. No expression is evaluated as executable code.
const matchTemplate = (source, template) => {
  if (!source.startsWith(template.prefix) || !source.endsWith(template.suffix)) return null;
  const values = [source]; let cursor = template.prefix.length;
  for (let index = 1; index < template.parts.length; index++) {
    const literal = template.parts[index], last = index === template.parts.length - 1;
    const end = last ? source.length - literal.length : source.indexOf(literal, cursor);
    if (end < cursor) return null;
    values.push(source.slice(cursor, end)); cursor = end + literal.length;
  }
  return cursor === source.length ? values : null;
};
const translated = new Map();
export function translateText(value, language = getLanguage(), depth = 0) {
  if (typeof value !== 'string' || normalizeLanguage(language) === 'it' || !value.trim()) return value;
  const cacheKey = `${language}:${value}`;
  if (translated.has(cacheKey)) return translated.get(cacheKey);
  const source = normaliseText(value);
  if (/^[\d\s.,+−\-–/%€:KMB×x°()]+$/.test(source)) return value;
  const exact = bySource.get(source);
  let result;
  if (exact) result = i18n.getFixedT(normalizeLanguage(language), exact.namespace || 'common')(exact.id);
  else if (depth < 3) {
    for (const template of templates) {
      if ((template.prefix && !source.startsWith(template.prefix)) || (template.suffix && !source.endsWith(template.suffix))) continue;
      const match = matchTemplate(source, template);
      if (!match) continue;
      const values = Object.fromEntries(template.keys.map((key, index) => [key, translateText(match[index + 1], language, depth + 1)]));
      result = i18n.getFixedT(normalizeLanguage(language), template.message.namespace || 'common')(template.message.id, values);
      break;
    }
  }
  if (result == null) {
    if (translated.size >= 8192) translated.clear();
    translated.set(cacheKey, value); return value;
  }
  // Preserve spaces separating inline JSX fragments.
  result = (value.match(/^\s*/)?.[0] || '') + result + (value.match(/\s*$/)?.[0] || '');
  if (translated.size >= 8192) translated.clear();
  translated.set(cacheKey, result);
  return result;
}
export const trDisplay = value => typeof value === 'string' ? translateText(value) : value;
// Interpolated user text is literal data, even when it happens to equal a UI label.
export function trTemplate(source, values, language = getLanguage()) {
  const message = bySource.get(normaliseText(source));
  if (message) return i18n.getFixedT(normalizeLanguage(language), message.namespace || 'common')(message.id, values);
  return source.replace(/\{\{(\w+)\}\}/g, (_, key) => String(values[key] ?? ''));
}
export const localizedFormatter = formatter => typeof formatter !== 'function' ? trDisplay(formatter) : (...args) => {
  const value = formatter(...args);
  return Array.isArray(value) ? value.map(trDisplay) : trDisplay(value);
};
export const messageDescriptor = (code, values = {}, original = '') => ({ code, values, original });
export function describeText(original) {
  if (typeof original !== 'string') return { code: null, values: {}, original };
  const source = normaliseText(original), exact = bySource.get(source);
  if (exact) return messageDescriptor(`${exact.namespace || 'common'}:${exact.id}`, {}, original);
  for (const template of templates) {
    if ((template.prefix && !source.startsWith(template.prefix)) || (template.suffix && !source.endsWith(template.suffix))) continue;
    const match = matchTemplate(source, template);
    if (match) return messageDescriptor(`${template.message.namespace || 'common'}:${template.message.id}`,
      Object.fromEntries(template.keys.map((key, index) => [key, match[index + 1]])), original);
  }
  return { code: null, values: {}, original };
}
export const translateDescriptor = descriptor => descriptor?.code
  ? i18n.t(descriptor.code, { ...Object.fromEntries(Object.entries(descriptor.values || {}).map(([key, value]) => [key, translateText(value)])), defaultValue: translateText(descriptor.original) })
  : translateText(descriptor?.original ?? descriptor);

const numberFormats = new Map();
export const numberFormatter = (options = {}, language = getLanguage()) => {
  const key = JSON.stringify([language, options]);
  if (!numberFormats.has(key)) {
    if (numberFormats.size >= 128) numberFormats.clear();
    numberFormats.set(key, new Intl.NumberFormat(localeFor(language), { useGrouping: 'always', ...options }));
  }
  return numberFormats.get(key);
};

// Pure-date formatting uses UTC so a locale change cannot shift the calendar day.
export const formatDate = value => {
  if (!value || !/^\d{4}-\d{2}(?:-\d{2})?$/.test(value)) return value || '';
  const monthly = value.length === 7, date = new Date(value + (monthly ? '-01T12:00:00Z' : 'T12:00:00Z'));
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(getLocale(), { timeZone:'UTC', year:'numeric', month:monthly?'short':'2-digit', ...(monthly?{}:{day:'2-digit'}) }).format(date);
};
